import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {useFormikContext} from 'formik';

interface FaceDraft {
    index: number;
    text: string;
}

export interface HeldFace {
    index: number;
    message: string;
}

interface HeldEntry extends HeldFace {
    text: string;
    layout: string;
}

export interface FaceLimits {
    // why a height is turned away, or null when it fits
    check: (index: number, height: number) => string;
    // the heights the form holds; once they change, held entries are stale
    layout: string;
    onHold: (held: HeldFace[]) => void;
    onReject: (index: number) => void;
}

interface FaceDraftType {
    textFor: (index: number) => string;
    setDraft: (index: number, text: string) => void;
    clearDraft: () => void;
    // false for an entry that does not fit, which stays in the field but out
    // of the form
    accept: (
        index: number,
        height: number,
        text: string,
        current: number
    ) => boolean;
    isHeld: (index: number) => boolean;
    // saves the product once what was just committed has reached the form
    save: () => void;
}

const FaceDraftContext = createContext<FaceDraftType>({
    textFor: () => null,
    setDraft: () => undefined,
    clearDraft: () => undefined,
    accept: () => true,
    isHeld: () => false,
    save: () => undefined,
});

export const useFaceDraft = (): FaceDraftType => useContext(FaceDraftContext);

interface FaceDraftProviderProps {
    // without them any height goes
    limits?: FaceLimits;
}

// Half-typed height, shared by the box on the drawing and the panel field.
// Never written to the form: "2" on the way to "250" is a face no runner fits.
export const FaceDraftProvider = ({
    limits,
    children,
}: React.PropsWithChildren<FaceDraftProviderProps>) => {
    const [draft, setDraft] = useState<FaceDraft>(null);
    const [held, setHeld] = useState<HeldEntry[]>([]);
    const {submitForm} = useFormikContext();
    const [saveRequested, setSaveRequested] = useState(false);

    // read straight after a commit, before the held entries have rendered
    const heldIndexes = useRef<number[]>([]);

    useEffect(() => {
        heldIndexes.current = held.map((entry) => entry.index);
    }, [held]);

    useEffect(() => {
        if (!saveRequested) return;

        setSaveRequested(false);
        void submitForm();
    }, [saveRequested, submitForm]);

    const layout = limits?.layout;
    const onHold = limits?.onHold;

    useEffect(() => {
        setHeld((entries) =>
            entries.some((entry) => entry.layout !== layout)
                ? entries.filter((entry) => entry.layout === layout)
                : entries
        );
    }, [layout]);

    useEffect(() => {
        onHold?.(held.map(({index, message}) => ({index, message})));
    }, [held, onHold]);

    const textFor = useCallback(
        (index: number) => {
            if (draft?.index === index) return draft.text;

            return held.find((entry) => entry.index === index)?.text ?? null;
        },
        [draft, held]
    );

    const set = useCallback(
        (index: number, text: string) => setDraft({index, text}),
        []
    );

    const clear = useCallback(() => setDraft(null), []);

    const accept = useCallback(
        (index: number, height: number, text: string, current: number) => {
            // leaving a face at what it has is not an edit
            const message =
                limits && height !== current
                    ? limits.check(index, height)
                    : null;
            const others = (entries: HeldEntry[]) =>
                entries.filter((entry) => entry.index !== index);

            heldIndexes.current = [
                ...heldIndexes.current.filter((held) => held !== index),
                ...(message === null ? [] : [index]),
            ];

            if (message === null) {
                setHeld((entries) =>
                    entries.some((entry) => entry.index === index)
                        ? others(entries)
                        : entries
                );

                return true;
            }

            setHeld((entries) => [
                ...others(entries),
                {index, message, text, layout: limits.layout},
            ]);
            limits.onReject(index);

            return false;
        },
        [limits]
    );

    const isHeld = useCallback(
        (index: number) => heldIndexes.current.includes(index),
        []
    );

    const save = useCallback(() => setSaveRequested(true), []);

    const value = useMemo(
        () => ({
            textFor,
            setDraft: set,
            clearDraft: clear,
            accept,
            isHeld,
            save,
        }),
        [textFor, set, clear, accept, isHeld, save]
    );

    return (
        <FaceDraftContext.Provider value={value}>
            {children}
        </FaceDraftContext.Provider>
    );
};
